import { useEffect, useId, useMemo, useState } from "react";
import { Button, Container, FormGroup, Input, Label } from "reactstrap";
import { Api, SQLEnum } from "../../../shared";
import { Tax } from "../../../shared/api";
import { CapitalizeFirst } from "../../../shared/helpers/StringHelpers";
import { App } from "../../App";
import { TaxList } from "./TaxList";
import { OptionList } from "../../../component";

export type EditCountryProps = {
	country: Tax.CountryType;
	taxes: Tax.TaxType[];
	on_save: () => any;
};

export type AddCountryProps = {
	on_save: () => any;
};

type CountryBaseProps = {
	country: Tax.CountryType;
	taxes: Tax.TaxType[];
	on_save_country: (country: Tax.CountryType) => any;
	on_save_tax?: (tax: Tax.TaxType) => any;
	on_delete_tax?: (tax: Tax.TaxType) => any;
	is_edit: boolean;
};

const CountryPostOpts = (props: {opts: Api.Post.CourierType[]}) => {
	let elements: JSX.Element[] = [];
	for(let opt of props.opts) {
		elements.push(<option key={opt.code} value={opt.code}>{opt.description}</option>);
	}
	return <>{elements}</>;
}

type info_t = {
	countries_post: Api.Post.CourierType[];
	currencies: string[];
}

const CountryBase = (props: CountryBaseProps) => {

	const [country, set_country] = useState<Tax.CountryType>(() => ({...props.country}));
	const [enable_tax_min, set_enable_tax_min] = useState(props.country.tax_threshold_min !== null);
	const [enable_tax_max, set_enable_tax_max] = useState(props.country.tax_threshold_max !== null);

	const [info, set_info] = useState<info_t | null>(null);

	const check_is_valid = (country: Tax.CountryType) => (
		(!enable_tax_min || (
			country.tax_threshold_min !== null &&
			isFinite(country.tax_threshold_min) &&
			country.tax_threshold_min >= 0
		)) &&
		(!enable_tax_max || (
			country.tax_threshold_max !== null &&
			isFinite(country.tax_threshold_max) &&
			country.tax_threshold_max >= 0
		)) &&
		country.tax_type.length > 0 &&
		country.display_fmt.includes("{}") &&
		props.taxes.length > 0
	);

	const do_full_validity = (country.state === "TAXED" || check_is_valid(props.country));

	const is_valid = (
		country.label.length > 0 &&
		country.code.length === 2 &&
		country.currency.length === 3 &&
		(!do_full_validity || check_is_valid(country))
	);

	const id = {
		label: useId(),
		tax_type: useId(),
		display_fmt: useId(),
		country: useId(),
		currency: useId(),
		tax_min: useId(),
		tax_max: useId(),
		enable_tax_min: useId(),
		enable_tax_max: useId(),
		state: useId(),
	};

	useEffect(() => {(async () => {

		let [countries_post, currencies] = await Promise.all([Api.Post.Call.countries({getAll: "1"}), Api.Staff.Tax.Call.currencyCodes({})]);

		if(!countries_post.data) {
			throw countries_post.error;
		}
		if(!currencies.data) {
			throw currencies.error;
		}
		let info: info_t = {
			countries_post: countries_post.data.countries,
			currencies: currencies.data.codes,
		};

		set_info(info);
		console.log(info);

	})()}, []);

	const update = (v: Partial<Tax.CountryType>) => {
		set_country({...country, ...v});
	}

	const btn_save = async () => {
		if(await props.on_save_country(country)) {
			App.open(null);
		}
	}
	const btn_back = () => {
		App.open(null);
	}

	const e_status_opts: JSX.Element[] = [];
	for(let status of SQLEnum.A.CountryState) {
		e_status_opts.push(<option key={status} value={status}>{CapitalizeFirst(status)}</option>);
	}

	return (
		<Container>
			<h4>{props.is_edit ? "Edit" : "Add"} Country</h4>
			<FormGroup>
				<Label htmlFor={id.label}>Country Name</Label>
				<Input type="text"
					id={id.label}
					defaultValue={props.country.label}
					onChange={e => update({label: e.target.value})}
				/>
			</FormGroup>
			<FormGroup>
				<Label htmlFor={id.country}>Country (Australia Post)</Label>
				{info ? <Input type="select"
					id={id.country}
					defaultValue={props.country.code} 
					onChange={e => update({code: e.target.value})}
				>
					<option></option>
					<CountryPostOpts opts={info.countries_post} />
				</Input> : <></>}
			</FormGroup>
			<FormGroup>
				<Label htmlFor={id.currency}>Currency Code</Label>
				{info ? <Input type="select" 
					id={id.currency}
					defaultValue={props.country.currency} 
					onChange={e => update({currency: e.target.value})} 
				>
					<option></option>
					<OptionList opts={info.currencies} />
				</Input> : <></>}
			</FormGroup>
			<FormGroup>
				<Label htmlFor={id.state}>Status</Label>
				<Input type="select"
					id={id.state}
					defaultValue={props.country.state}
					onChange={e => update({state: e.target.value as SQLEnum.CountryState})}>
					{e_status_opts}
				</Input>
			</FormGroup>
			{do_full_validity ? <>
				<FormGroup>
					<Label htmlFor={id.tax_type}>Tax Name</Label>
					<Input type="text" 
						id={id.tax_type}
						defaultValue={props.country.tax_type} 
						onChange={e => update({tax_type: e.target.value})} 
					/>
				</FormGroup>
				<FormGroup>
					<Label htmlFor={id.display_fmt}>Currency Display Format</Label>
					<Input type="text" 
						id={id.display_fmt}
						defaultValue={props.country.display_fmt} 
						placeholder="${} AUD" 
						onChange={e => update({display_fmt: e.target.value})} 
					/>
				</FormGroup>
				<FormGroup>
					<Label htmlFor={id.tax_max}>Upper Taxation Threshold</Label>
					<Input type="number" 
						id={id.tax_max}
						defaultValue={props.country.tax_threshold_max ?? ""} 
						onChange={e => update({tax_threshold_max: parseFloat(e.target.value)})} 
						disabled={!enable_tax_max}
					/>
					<Input type="checkbox"
						id={id.enable_tax_max}
						defaultChecked={enable_tax_max}
						onChange={e => {
							set_enable_tax_max(e.target.checked);
							update({tax_threshold_max: null});
						}}
					/> <Label htmlFor={id.enable_tax_max}>Enable</Label>
				</FormGroup>
				<FormGroup>
					<Label htmlFor={id.tax_min}>Lower Taxation Threshold</Label>
					<Input type="number" 
						id={id.tax_min}
						defaultValue={props.country.tax_threshold_min ?? ""} 
						onChange={e => update({tax_threshold_min: parseFloat(e.target.value)})} 
						disabled={!enable_tax_min}
					/>
					<Input type="checkbox"
						id={id.enable_tax_min}
						defaultChecked={enable_tax_min}
						onChange={e => {
							set_enable_tax_min(e.target.checked);
							update({tax_threshold_min: null});
						}}
					/> <Label htmlFor={id.enable_tax_min}>Enable</Label>
				</FormGroup>
				{props.on_save_tax ? <FormGroup>
					<TaxList country={country} taxes={props.taxes} on_save_tax={props.on_save_tax} on_delete_tax={props.on_delete_tax} />
				</FormGroup> : <></>}
			</> : <></>}
			<FormGroup>
				<Button onClick={btn_save} disabled={!is_valid}>Save</Button>
				<Button onClick={btn_back}>Back</Button>
			</FormGroup>
		</Container>
	);
}

export const EditCountry = (props: EditCountryProps) => {

	const save_country = async (country: Tax.CountryType) => {
		let res = await Api.Staff.Tax.Call.editCountry({country});
		if(res.error) {
			throw res.error;
		}
		return await props.on_save();
	}

	return <CountryBase
		country={props.country}
		taxes={props.taxes}
		on_save_country={save_country}
		is_edit={true}
	/>;
}

export const AddCountry = (props: AddCountryProps) => {
	
	const [taxes, set_taxes] = useState<Tax.TaxType[]>([]);
	
	const save_country = async (country: Tax.CountryType) => {
		let res = await Api.Staff.Tax.Call.add({country, taxes});
		if(res.error) {
			throw res.error;
		}
		return await props.on_save();
	}
	const save_tax = (tax: Tax.TaxType) => {
		if(tax.id !== null) {
			taxes[tax.id] = tax;
		} else {
			tax.id = taxes.length;
			taxes.push(tax);
		}
		set_taxes(taxes);
		return true;
	}
	const delete_tax = (tax_d: Tax.TaxType) => {
		let taxes_new: Tax.TaxType[] = [];
		for(let [i, tax] of taxes.entries()) {
			if(tax_d.id !== i) {
				tax.id = taxes_new.length;
				taxes_new.push(tax);
			}
		}
		set_taxes(taxes_new);
		return true;
	}

	return <CountryBase
		country={{
			code: "",
			label: "",
			currency: "",
			tax_type: "",
			tax_id: 0,
			tax_threshold_min: null,
			tax_threshold_max: null,
			state: "UNTAXED",
			display_fmt: "",
		}}
		taxes={taxes}
		on_save_country={save_country}
		on_save_tax={save_tax}
		on_delete_tax={delete_tax}
		is_edit={false}
	/>;
}

